'use client';

import { CalendarIcon } from '@heroicons/react/24/outline';
import { DAY_FIRST_DATE_INPUT_FORMATS, parseDayFirstDate } from '@koda/koda-utils/date';
import dayjs from 'dayjs';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange, Matcher } from 'react-day-picker';
import { cn } from '../../lib/utils/classnameVariants';
import { StateIcon } from '../../lib/utils/forms';
import { useDialogPortalContainer } from '../../lib/utils/hooks/useDialogPortalContainer';
import { FormLabel } from '../atoms/FormLabel';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Description } from './TextField';

const INPUT_DATE_FORMAT = 'DD-MM-YYYY';

// split into segments to allow for auto-formatting while typing, e.g. "dd-mm-yyyy" or "dd-mm-yyyy - dd-mm-yyyy"
export const formatDateSegmentForTyping = (value: string, appendTrailingSeparator: boolean): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits.length === 2 && appendTrailingSeparator ? `${digits}-` : digits;

  const dayMonth = `${digits.slice(0, 2)}-${digits.slice(2)}`;
  if (digits.length <= 4) return digits.length === 4 && appendTrailingSeparator ? `${dayMonth}-` : dayMonth;

  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

export const formatDateInputForTyping = (
  rawInput: string,
  selectionMode: DatePickerProps['selectionMode'],
  appendTrailingSeparator: boolean,
): string => {
  if (selectionMode === 'single') {
    return formatDateSegmentForTyping(rawInput, appendTrailingSeparator);
  }

  const normalized = rawInput.replace(/[–—]/g, '-');
  const rangeParts = normalized.split(/\s-\s/);
  if (rangeParts.length < 2) {
    return formatDateSegmentForTyping(normalized, appendTrailingSeparator);
  }

  const from = formatDateSegmentForTyping(rangeParts[0] ?? '', appendTrailingSeparator);
  const to = formatDateSegmentForTyping(rangeParts[1] ?? '', appendTrailingSeparator);
  return `${from} - ${to}`.trim();
};

export interface DatePickerProps {
  /** Selection mode: single date, range of dates, or user toggle between both */
  selectionMode?: 'single' | 'range' | 'both';
  /** Controlled selected date (single mode) */
  value?: Date;
  /** Controlled selected range (range mode) */
  rangeValue?: { from?: Date; to?: Date };
  /** Callback when single date changes */
  onChange?: (date: Date | undefined) => void;
  /** Callback when range changes */
  onRangeChange?: (range: { from?: Date; to?: Date } | undefined) => void;
  /** Placeholder text shown in the input when no date selected */
  placeholder?: string;
  /** Uncontrolled initial date */
  defaultValue?: Date;
  /** Optional input id */
  id?: string;
  /** Optional input name */
  name?: string;
  /** Disable user interaction */
  disabled?: boolean;
  /** Called when the popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Whether the popover should close immediately after a date is selected in single mode (default: false) */
  closeOnSelect?: boolean;
  /** Label shown above the field */
  label: string;
  /** Whether to visually hide the label */
  hideLabel?: boolean;
  /** Optional helper text shown below when no error or while focused */
  helpText?: string;
  /** Optional text shown to the right of the label (e.g. Optional) */
  optionalText?: string;
  /** Validation: show green check mark when true (and no error) */
  validated?: boolean;
  /** Error message (also controls error state + icon) */
  error?: string;
  /** Initial value to show when no date is selected (defaults to 30 years back from today) */
  initialVisibleValue?: Date;
  /** Date interval that can be selected (dates outside this range will be disabled) */
  interval?: { from: Date; to: Date };
  /** Optional label for single selection toggle (delegated to Calendar) */
  singleLabel?: string;
  /** Optional label for range selection toggle (delegated to Calendar) */
  rangeLabel?: string;
  locale?: 'da' | 'en';
  /** Whether the input should be focused on mount */
  autoFocus?: boolean;
  /** Called after a manually typed date is successfully parsed — return an error string to reject it, or undefined to accept */
  validateDate?: (date: Date) => string | undefined;
  /** How the calendar is rendered: overlaying popover, toggled in document flow, or always visible in document flow */
  calendarPlacement?: 'popover' | 'inline' | 'alwaysOpen';
}

// (Typing disabled so validation helper removed previously)

export function DatePicker({
  selectionMode = 'single',
  value: valueProp,
  rangeValue,
  onChange,
  onRangeChange,
  placeholder,
  id = 'date',
  name,
  disabled,
  onOpenChange,
  closeOnSelect = true,
  label,
  hideLabel,
  helpText,
  optionalText,
  validated,
  error,
  initialVisibleValue = new Date(new Date().getFullYear() - 30, new Date().getMonth(), 1),
  interval,
  locale = 'da',
  autoFocus,
  validateDate,
  calendarPlacement = 'popover',
}: DatePickerProps) {
  const isInline = calendarPlacement !== 'popover';
  const isAlwaysOpen = calendarPlacement === 'alwaysOpen';
  // Prevent immediate re-open when focus returns to the input right after selecting a date
  const skipFocusOpenRef = useRef(false);
  const { wrapperRef, portalContainer } = useDialogPortalContainer<HTMLDivElement>();
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [manualInputValue, setManualInputValue] = useState<string | null>(null);
  const [manualInputError, setManualInputError] = useState<string | undefined>();
  const rangeSelectionCountRef = useRef(0);

  const date = valueProp;
  const range = rangeValue as DateRange | undefined;
  const [activeMode, setActiveMode] = useState<'single' | 'range'>(selectionMode === 'range' ? 'range' : 'single');
  useEffect(() => {
    if (selectionMode === 'single') setActiveMode('single');
    else if (selectionMode === 'range') setActiveMode('range');
  }, [selectionMode]);
  const [month, setMonth] = useState<Date | undefined>(
    date ? new Date(date.getFullYear(), date.getMonth(), 1) : initialVisibleValue,
  );

  // Separate display state so UI updates instantly even in controlled mode (optimistic UI)
  const [displayDate, setDisplayDate] = useState<Date | undefined>(date);
  useEffect(() => {
    // Sync whenever external value changes
    setDisplayDate(date);
    setManualInputValue(null);
    setManualInputError(undefined);
  }, [date]);

  // Keep visible month in sync ONLY when the selected date itself changes.
  // (Previously also reacted to `month` which caused user navigation to be overridden.)
  useEffect(() => {
    if (date) {
      const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      setMonth(prev => {
        if (!prev) return firstOfMonth;
        if (prev.getFullYear() !== firstOfMonth.getFullYear() || prev.getMonth() !== firstOfMonth.getMonth()) {
          return firstOfMonth;
        }
        return prev; // no change
      });
    }
  }, [date]);

  const displayValue = useMemo(() => {
    // Both mode: treat partial range as single, same-day range as single, multi-day range as range
    if (selectionMode === 'both') {
      if (range?.from && range?.to) {
        if (dayjs(range.from).isSame(range.to, 'day')) return dayjs(range.from).format(INPUT_DATE_FORMAT);
        const fromStr = dayjs(range.from).format(INPUT_DATE_FORMAT);
        const toStr = dayjs(range.to).format(INPUT_DATE_FORMAT);
        return `${fromStr} - ${toStr}`;
      }
      if (range?.from) return dayjs(range.from).format(INPUT_DATE_FORMAT);
      return '';
    }
    if (activeMode === 'single') return displayDate ? dayjs(displayDate).format(INPUT_DATE_FORMAT) : '';
    if (range?.from && range?.to) {
      const fromStr = dayjs(range.from).format(INPUT_DATE_FORMAT);
      const toStr = dayjs(range.to).format(INPUT_DATE_FORMAT);
      return `${fromStr} - ${toStr}`;
    }
    if (range?.from) return dayjs(range.from).format(INPUT_DATE_FORMAT);
    return '';
  }, [selectionMode, activeMode, displayDate, range]);

  // Convert interval to disabled matcher for react-day-picker
  const disabledMatcher: Matcher | undefined = useMemo(() => {
    if (!interval) return undefined;
    return (date: Date) => {
      const fromTime = interval.from.getTime();
      const toTime = interval.to.getTime();
      const dateTime = date.getTime();
      return dateTime < fromTime || dateTime > toTime;
    };
  }, [interval]);

  const effectiveError = manualInputError ?? error;
  const showErrorState = !isFocused && !!effectiveError;
  const showValidatedState = !showErrorState && !isFocused && !!validated;

  useEffect(() => {
    if (open) rangeSelectionCountRef.current = 0;
  }, [open]);

  useEffect(() => {
    if (!open || isInline) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (wrapperRef.current?.contains(target)) return;
      if (popoverContentRef.current?.contains(target)) return;
      if (
        target.closest('[data-select-content]') ||
        target.closest('[data-select-trigger]') ||
        target.closest('[role="listbox"]')
      ) {
        return;
      }
      setOpen(false);
      onOpenChange?.(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, isInline, onOpenChange, wrapperRef]);

  function getFormatErrorMessage() {
    const formats = DAY_FIRST_DATE_INPUT_FORMATS;
    const lastFormat = formats[formats.length - 1];
    const leadingFormats = formats.slice(0, -1).join(', ');
    const singleDateHint =
      locale === 'da'
        ? `Indtast en gyldig dato som ${leadingFormats} eller ${lastFormat}`
        : `Enter a valid date as ${leadingFormats}, or ${lastFormat}`;

    if (selectionMode === 'single') return `${singleDateHint}.`;
    return locale === 'da'
      ? `${singleDateHint}, eller intervallet dd-mm-yyyy - dd-mm-yyyy.`
      : `${singleDateHint}, or range as dd-mm-yyyy - dd-mm-yyyy.`;
  }
  function applyManualInput() {
    const rawInput = (manualInputValue ?? displayValue).trim();
    if (rawInput.length === 0) {
      handleClear();
      setManualInputValue(null);
      setManualInputError(undefined);
      return;
    }

    const normalizedInput = rawInput.replace(/[–—]/g, '-').replace(/\s+/g, ' ');
    const rangeValues = normalizedInput.split(/\s-\s/).map(part => part.trim());
    const rangeSeparatorPresent = /\s-\s/.test(normalizedInput);

    if (selectionMode === 'single') {
      const parsedDate = parseDayFirstDate(normalizedInput);
      if (!parsedDate.isValid()) {
        setManualInputError(getFormatErrorMessage());
        return;
      }

      const nextDate = parsedDate.toDate();
      const validationError = validateDate?.(nextDate);
      if (validationError) {
        setManualInputError(validationError);
        return;
      }

      setDisplayDate(nextDate);
      onChange?.(nextDate);
      setMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
      setManualInputValue(null);
      setManualInputError(undefined);
      return;
    }

    if (rangeSeparatorPresent) {
      const [fromRaw, toRaw, ...rest] = rangeValues;
      if (!fromRaw || !toRaw || rest.length > 0) {
        setManualInputError(getFormatErrorMessage());
        return;
      }

      const fromDate = parseDayFirstDate(fromRaw);
      const toDate = parseDayFirstDate(toRaw);
      if (!fromDate.isValid() || !toDate.isValid()) {
        setManualInputError(getFormatErrorMessage());
        return;
      }

      const nextFrom = toDate.isBefore(fromDate, 'day') ? toDate.toDate() : fromDate.toDate();
      const nextTo = toDate.isBefore(fromDate, 'day') ? fromDate.toDate() : toDate.toDate();
      const validationError = validateDate?.(nextFrom) ?? validateDate?.(nextTo);
      if (validationError) {
        setManualInputError(validationError);
        return;
      }

      onRangeChange?.({ from: nextFrom, to: nextTo });
      setMonth(new Date(nextFrom.getFullYear(), nextFrom.getMonth(), 1));
      setManualInputValue(null);
      setManualInputError(undefined);
      return;
    }

    const parsedSingleRangeDate = parseDayFirstDate(normalizedInput);
    if (!parsedSingleRangeDate.isValid()) {
      setManualInputError(getFormatErrorMessage());
      return;
    }

    const nextSingleRangeDate = parsedSingleRangeDate.toDate();
    const validationError = validateDate?.(nextSingleRangeDate);
    if (validationError) {
      setManualInputError(validationError);
      return;
    }

    onRangeChange?.({ from: nextSingleRangeDate, to: undefined });
    setMonth(new Date(nextSingleRangeDate.getFullYear(), nextSingleRangeDate.getMonth(), 1));
    setManualInputValue(null);
    setManualInputError(undefined);
  }

  function handleSelectSingle(selected: Date | undefined) {
    setDisplayDate(selected);
    onChange?.(selected);
    setManualInputValue(null);
    setManualInputError(undefined);
    // In 'both' mode we keep the popover open after the first (start) click
    if (selectionMode === 'both') return;
    if (closeOnSelect) {
      skipFocusOpenRef.current = true;
      setOpen(false);
      setTimeout(() => {
        skipFocusOpenRef.current = false;
      }, 150);
    }
  }
  function handleSelectRange(selected: DateRange | undefined) {
    if (
      selectionMode === 'both' &&
      selected?.from &&
      selected?.to &&
      range?.from &&
      !range?.to &&
      rangeSelectionCountRef.current === 0 &&
      !dayjs(selected.from).isSame(selected.to, 'day')
    ) {
      const replacementDate = selected.to;
      onRangeChange?.({ from: replacementDate, to: undefined });
      setMonth(new Date(replacementDate.getFullYear(), replacementDate.getMonth(), 1));
      setManualInputValue(null);
      setManualInputError(undefined);
      rangeSelectionCountRef.current += 1;
      return;
    }

    onRangeChange?.(selected);
    if (selected?.from && !selected?.to) setMonth(new Date(selected.from.getFullYear(), selected.from.getMonth(), 1));
    setManualInputValue(null);
    setManualInputError(undefined);
    rangeSelectionCountRef.current += 1;
  }

  function handleClear() {
    if (activeMode === 'single') {
      onChange?.(undefined);
      setDisplayDate(undefined);
    } else onRangeChange?.(undefined);
    setManualInputError(undefined);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  // Input handling: Allow for auto-formatting, while user is typing.
  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const nextValue = e.target.value;
    const cursorPosition = e.target.selectionStart ?? nextValue.length;
    const isCursorAtEnd = cursorPosition === nextValue.length;
    const inputType = (e.nativeEvent as InputEvent | undefined)?.inputType ?? '';
    const isInsertAction = inputType.startsWith('insert');

    // Only mask while appending at the end; keep raw value for in-place edits/deletions.
    if (!isCursorAtEnd || !isInsertAction) {
      setManualInputValue(nextValue);
      setManualInputError(undefined);
      return;
    }

    setManualInputValue(formatDateInputForTyping(nextValue, selectionMode, true));
    setManualInputError(undefined);
  }

  const calendar = (
    <Calendar
      disabled={disabledMatcher}
      interval={interval}
      selectionMode={selectionMode}
      singleSelected={selectionMode === 'both' ? undefined : date}
      rangeSelected={range?.from ? { from: range.from, to: range.to } : undefined}
      onSingleSelect={selectionMode === 'both' ? undefined : handleSelectSingle}
      onRangeSelect={handleSelectRange}
      month={month}
      defaultMonth={initialVisibleValue}
      onMonthChange={setMonth}
      effectiveLocale={locale}
      portalContainer={portalContainer}
    />
  );

  const triggerButton = (
    <Button
      disabled={disabled}
      id="date-picker"
      variant="ghost"
      className="absolute right-2 top-1/2 size-6 -translate-y-1/2"
      aria-expanded={isInline ? open : undefined}
      aria-controls={isInline ? 'date-picker-popover' : undefined}
      onClick={isInline ? () => handleOpenChange(!open) : undefined}
    >
      <CalendarIcon className="size-4" />
      <span className="sr-only">Select date</span>
    </Button>
  );

  const inputField = (
    <Input
      id={id}
      value={manualInputValue ?? displayValue}
      placeholder={placeholder}
      name={name}
      className={cn(
        // Match legacy TextField / Input styling and enforce larger font size
        'rounded-sm border border-black-100 focus:border-black-900 focus-visible:outline-none focus-visible:ring-0',
        'pr-14 font-graphik text-md md:text-md',
      )}
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={handleInputChange}
      onFocus={() => {
        setIsFocused(true);
      }}
      onBlur={() => {
        setIsFocused(false);
        applyManualInput();
      }}
      onKeyDown={e => {
        if (disabled) return;
        if (e.key === 'Enter') {
          e.preventDefault();
          applyManualInput();
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (skipFocusOpenRef.current) return;
          setOpen(true);
        }
        if (e.key === 'Escape' && isInline && !isAlwaysOpen) {
          e.preventDefault();
          handleOpenChange(false);
        }
      }}
      aria-haspopup={isAlwaysOpen ? undefined : 'dialog'}
      aria-expanded={isAlwaysOpen ? undefined : open}
      aria-controls="date-picker-popover"
    />
  );

  const descriptions = (
    <>
      {!isFocused && effectiveError && <Description isError description={effectiveError} />}
      {helpText && (!effectiveError || isFocused) && <Description description={helpText} />}
    </>
  );

  return (
    <div ref={wrapperRef} className="flex flex-col">
      <div className={cn(disabled && 'opacity-50', 'flex items-start justify-between')}>
        {label && !hideLabel && <FormLabel title={label} />}
        {optionalText && <p className="my-auto font-graphik text-xs font-normal text-black-300">{optionalText}</p>}
      </div>
      <div className="relative my-2 flex flex-col gap-2">
        <div className="relative flex gap-2">
          {inputField}
          {(showErrorState || showValidatedState) &&
            StateIcon(showErrorState, showValidatedState, 'top-1/2 -translate-y-1/2 right-10')}
          {isInline ? (
            isAlwaysOpen ? (
              <CalendarIcon
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 stroke-black-300',
                  disabled && 'opacity-50',
                )}
              />
            ) : (
              triggerButton
            )
          ) : (
            <Popover open={open} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
              <PopoverContent
                ref={popoverContentRef}
                id="date-picker-popover"
                className="popover-content-calendar w-auto overflow-hidden p-0"
                align="end"
                alignOffset={-8}
                sideOffset={10}
                collisionPadding={16}
                portalContainer={portalContainer}
                onInteractOutside={e => {
                  const target = e.target as HTMLElement | null;
                  if (target?.closest('[data-select-content]') || target?.closest('[data-select-trigger]')) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="calendar-wrapper max-h-[var(--radix-popover-content-available-height)] overflow-y-auto">
                  {calendar}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        {isInline && descriptions}
        {isInline && (isAlwaysOpen || open) && (
          <div
            id="date-picker-popover"
            className="calendar-wrapper mt-2 flex justify-center overflow-hidden rounded-md border border-black-50 bg-white"
          >
            {calendar}
          </div>
        )}
      </div>
      {!isInline && descriptions}
    </div>
  );
}
